/**
 * "I tuoi workspace" + "Aggiungi workspace" (ADR-0076 fetta 5) — the cross-deployment card
 * inside the Abbonamento settings section.
 *
 * The list is the caller's OWN identity memberships (ADR-0077 revised decision 7), so it
 * shows exactly the workspaces they can enter — never everything the company pays for. Both
 * halves are gated on the dedicated `organization` permission; creating one commits money,
 * so it is `organization:write` and never `settings:write`.
 *
 * A local-only account has no central identity: the backend answers 422
 * `organization:requires_central_account` and we render the honest note instead of an empty
 * table.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { type BillingPeriod, billingApi } from "@/lib/api-billing";
import { BillingFieldset } from "@/components/billing/BillingFieldset";
import {
  type BillingForm,
  EMPTY_BILLING,
  billingComplete,
  toBillingPayload,
} from "@/lib/billingProfile";
import { organizationApi } from "@/lib/api-organization";
import { usePermissions } from "@/lib/permissions";

/** Mirror of the backend slug rule, so a bad name is caught before the round-trip. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

export function OrgWorkspaces() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canRead = can("organization", "read");
  const canCreate = can("organization", "write");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [planKey, setPlanKey] = useState("");
  const [period, setPeriod] = useState<BillingPeriod>("month");

  const q = useQuery({
    queryKey: ["org-workspaces"],
    queryFn: () => organizationApi.list(),
    enabled: canRead,
    retry: false,
  });
  // The plan picker reuses the SAME catalog the Abbonamento comparison shows.
  const plansQ = useQuery({
    queryKey: ["billing-overview"],
    queryFn: () => billingApi.overview(),
    enabled: canCreate,
  });

  // The purchase gate refuses a checkout with no invoiceable profile ("a chi facciamo
  // fattura?" — ADR-0079 dec.7), and each workspace carries its OWN profile (ADR-0076;
  // ADR-0080 abbandonata). Without this fieldset the door 422'd `billing:incomplete` on
  // EVERY create — the backend was ready, the dialog never asked.
  const [billing, setBilling] = useState<BillingForm>(EMPTY_BILLING);
  const [billingTouched, setBillingTouched] = useState(false);
  const setB = <K extends keyof BillingForm>(key: K, value: BillingForm[K]) =>
    setBilling((b) => ({ ...b, [key]: value }));
  const billingValid = billingComplete(billing);

  const create = useMutation({
    mutationFn: () =>
      organizationApi.create({
        slug,
        name: name || null,
        plan_key: planKey,
        billing_period: period,
        billing: toBillingPayload(billing),
      }),
    // The workspace does not exist yet (ADR-0079): what comes back is a Stripe Checkout
    // URL, so we LEAVE the SPA. The payment webhook is what provisions it — invalidating
    // the list here would only show the caller an unchanged list.
    onSuccess: (res) => {
      window.location.href = res.checkout_url;
    },
  });

  if (!canRead) return null;

  const needsCentral =
    q.error instanceof ApiError && q.error.message.includes("requires_central_account");
  const slugValid = SLUG_RE.test(slug);

  const openDialog = () => {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setPlanKey("");
    setPeriod("month");
    create.reset();
    setOpen(true);
  };

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            {t("organization.title", "I tuoi workspace")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t(
              "organization.subtitle",
              "Gli spazi di lavoro a cui puoi accedere con il tuo Account Tray.",
            )}
          </p>
        </div>
        {canCreate && !needsCentral && (
          <Button size="sm" onClick={openDialog}>
            {t("organization.add", "Aggiungi workspace")}
          </Button>
        )}
      </div>

      {needsCentral ? (
        <p className="text-sm text-muted-foreground">
          {t(
            "organization.requiresCentral",
            "Questo account è locale a questo workspace: per gestirne più di uno serve un Account Tray.",
          )}
        </p>
      ) : q.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>
      ) : q.error ? (
        <p className="text-sm text-danger">
          {t("organization.loadError", "Impossibile caricare i tuoi workspace.")}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {(q.data ?? []).map((w) => (
            <li key={w.tenant_id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {w.name}
                  {w.current && (
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                      {t("organization.thisWorkspace", "Questo workspace")}
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {w.slug}
                  {" · "}
                  {w.plan_name ?? t("organization.noPlan", "nessun piano")}
                </p>
              </div>
              {!w.current && (
                <a
                  className="shrink-0 text-sm text-primary hover:underline"
                  href={w.url}
                  rel="noreferrer"
                >
                  {t("organization.openWorkspace", "Apri")}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={(v) => !create.isPending && setOpen(v)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("organization.add", "Aggiungi workspace")}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="ws-name">{t("organization.name", "Nome")}</Label>
              <Input
                id="ws-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value));
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ws-slug">{t("organization.slug", "Indirizzo")}</Label>
              <Input
                id="ws-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase());
                }}
              />
              <p className="text-xs text-muted-foreground">
                {t("organization.slugHint", "Sarà il sottodominio del nuovo workspace.")}
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ws-plan">{t("organization.plan", "Piano")}</Label>
              <select
                id="ws-plan"
                className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                value={planKey}
                onChange={(e) => setPlanKey(e.target.value)}
              >
                <option value="">{t("organization.pickPlanOption", "Scegli un piano…")}</option>
                {(plansQ.data?.plans ?? []).map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            {planKey && (
              <div className="space-y-1">
                <Label htmlFor="ws-period">{t("organization.period", "Fatturazione")}</Label>
                <select
                  id="ws-period"
                  className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as BillingPeriod)}
                >
                  <option value="month">{t("billing.monthly", "Mensile")}</option>
                  <option value="year">{t("billing.yearly", "Annuale")}</option>
                </select>
              </div>
            )}
            {planKey && (
              <BillingFieldset t={t} billing={billing} setB={setB} showErrors={billingTouched} />
            )}
            {/* Said out loud, because the button leaves the app: this is a purchase, and
                it is the COMPANY's saved card that pays for it (ADR-0079). */}
            <p className="text-xs text-muted-foreground">
              {t(
                "organization.paymentNote",
                "Il nuovo workspace nasce sulla stessa organizzazione e viene addebitato sul suo metodo di pagamento. Ti portiamo alla cassa per confermare.",
              )}
            </p>
            {create.isError && (
              <p className="text-sm text-danger">
                {create.error instanceof ApiError && create.error.message.includes("slug_exists")
                  ? t("organization.slugTaken", "Questo indirizzo è già in uso.")
                  : create.error instanceof ApiError &&
                      create.error.message.includes("self_serve_unavailable")
                    ? t(
                        "organization.purchaseUnavailable",
                        "I pagamenti non sono configurati su questa installazione.",
                      )
                    : create.error instanceof ApiError &&
                        create.error.message.includes("billing:incomplete")
                      ? t(
                          "organization.billingIncomplete",
                          "Completa i dati di fatturazione per procedere.",
                        )
                      : create.error instanceof Error
                        ? create.error.message
                      : t("organization.createError", "Impossibile creare il workspace.")}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={create.isPending}>
              {t("common.cancel", "Annulla")}
            </Button>
            <Button
              onClick={() => {
                if (!billingValid) {
                  setBillingTouched(true);
                  return;
                }
                create.mutate();
              }}
              disabled={!slugValid || !planKey || create.isPending}
            >
              {create.isPending
                ? t("common.loading", "Caricamento…")
                : t("organization.buy", "Vai al pagamento")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
